import WaveDivider from "../../home/components/WaveDivider";
import type { ServicePageData } from "../../../mocks/servicePages";
import { bookingServices } from "../../../mocks/bookingData";

interface PricingSectionProps {
  accepts: string[];
  service: ServicePageData;
  onBookNow: () => void;
}

export default function PricingSection({ accepts, service, onBookNow }: PricingSectionProps) {
  const booking = bookingServices.find((b) => b.slug === service.slug);

  // ── Render: load / rooms — fixed-price option cards ─────────────────────────
  if (booking && (booking.stepType === "load" || booking.stepType === "rooms") && booking.options.length > 0) {
    const opts = booking.options;
    const gridClass =
      opts.length <= 2
        ? "grid-cols-1 sm:grid-cols-2 max-w-2xl mx-auto"
        : opts.length === 3
        ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto"
        : opts.length === 4
        ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl mx-auto"
        : opts.length === 5
        ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
        : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

    return (
      <div>
        <section className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
            <PricingHeader />

            <div className={`grid gap-5 mb-16 ${gridClass}`}>
              {opts.map((opt) => (
                <div
                  key={opt.id}
                  className={`rounded-3xl p-6 flex flex-col cartoon-card ${opt.id === "flexible" ? "bg-cl-dark" : opt.popular ? "bg-cl-dark" : "bg-white"}`}
                >
                  {opt.id === "flexible" && (
                    <div className="inline-flex items-center gap-1.5 bg-cl-green text-white text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4 w-fit border-2 border-[#4a8c0a] cartoon-shadow-sm">
                      <i className="ri-scales-3-line text-xs"></i>
                      Pay As You Go
                    </div>
                  )}
                  {opt.popular && opt.id !== "flexible" && (
                    <div className="inline-flex items-center gap-1.5 bg-cl-green text-white text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4 w-fit border-2 border-[#4a8c0a] cartoon-shadow-sm">
                      <i className="ri-star-fill text-xs"></i>
                      Most Popular
                    </div>
                  )}
                  {opt.wasPrice && (
                    <div className="inline-flex items-center gap-1.5 bg-red-100 text-red-600 text-xs font-black px-2 py-0.5 rounded-full mb-2 w-fit">
                      was {opt.wasPrice}
                    </div>
                  )}

                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl mb-3 border-2 ${opt.id === "flexible" ? "bg-cl-green/20 border-cl-dark" : opt.popular ? "bg-cl-green/20 border-cl-dark" : "bg-cl-lime border-cl-dark"}`}>
                    <i className={`${opt.icon} text-lg ${opt.id === "flexible" ? "text-cl-lime" : opt.popular ? "text-cl-lime" : "text-cl-dark"}`}></i>
                  </div>

                  <h3 className={`font-black text-lg mb-1 leading-tight ${opt.id === "flexible" ? "text-white" : opt.popular ? "text-white" : "text-cl-dark"}`}>
                    {opt.label}
                  </h3>
                  <p className={`text-xs font-semibold mb-4 leading-snug ${opt.id === "flexible" ? "text-white/60" : opt.popular ? "text-white/60" : "text-gray-400"}`}>
                    {opt.description}
                  </p>

                  <div className="flex items-end gap-1 mb-1">
                    <span className={`font-black text-3xl leading-none ${opt.id === "flexible" ? "text-cl-lime" : opt.popular ? "text-cl-lime" : "text-cl-dark"}`}>
                      {opt.priceLabel}
                    </span>
                  </div>
                  <p className={`text-xs font-semibold mb-5 ${opt.id === "flexible" ? "text-white/50" : opt.popular ? "text-white/50" : "text-gray-400"}`}>
                    inc. VAT
                  </p>

                  {opt.details && (
                    <ul className="flex flex-col gap-2 mb-6 flex-1">
                      {(opt.flexiBullets
                        ? opt.flexiBullets
                        : [
                            opt.details,
                            ...(opt.similarTo ? [`e.g. ${opt.similarTo}`] : []),
                            ...(opt.maxLabour ? [`~${opt.maxLabour} labour`] : []),
                          ]
                      ).map((feat) => (
                        <li key={feat} className="flex items-start gap-2">
                          <span className={`w-4 h-4 flex items-center justify-center rounded-full border-2 flex-shrink-0 mt-0.5 ${opt.popular ? "bg-cl-green border-[#4a8c0a]" : "bg-cl-lime border-cl-green"}`}>
                            <i className={`ri-check-line text-[9px] font-black ${opt.popular ? "text-white" : "text-cl-dark"}`}></i>
                          </span>
                          <span className={`text-xs font-semibold leading-snug ${opt.id === "flexible" ? "text-white/80" : opt.popular ? "text-white/80" : "text-cl-dark/80"}`}>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    onClick={onBookNow}
                    className={`w-full mt-auto font-black py-3 rounded-2xl text-sm whitespace-nowrap ${opt.id === "flexible" ? "bg-cl-green text-white cartoon-btn-green" : opt.popular ? "bg-cl-green text-white cartoon-btn-green" : "bg-cl-dark text-white cartoon-btn"}`}
                  >
                    Book Now →
                  </button>
                </div>
              ))}
            </div>

            <AcceptsPanel accepts={accepts} service={service} />
          </div>
        </section>
        <WaveDivider fromColor="#ffffff" toColor="#f3f4f8" flip />
      </div>
    );
  }

  // ── Render: items / bin — per-item price list grouped by category ────────────
  if (booking && (booking.stepType === "items" || booking.stepType === "bin") && booking.itemsConfig && booking.itemsConfig.length > 0) {
    const items = booking.itemsConfig;

    // Group by category
    const categories: Record<string, typeof items> = {};
    items.forEach((item) => {
      if (!categories[item.category]) categories[item.category] = [];
      categories[item.category].push(item);
    });
    const categoryNames = Object.keys(categories);

    const calloutCharge = booking.calloutCharge ?? 0;
    return (
      <div>
        <section className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
            <PricingHeader subtitle="Fixed price per item, collected and disposed of responsibly." />

            {/* Flexi / PAYG highlight banner */}
            {booking.slug === "rubbish-removal" && (
              <div className="mb-8 rounded-3xl overflow-hidden cartoon-card border-4 border-cl-dark">
                <div className="bg-cl-dark px-6 py-3 flex items-center gap-2">
                  <i className="ri-scales-3-line text-white text-lg"></i>
                  <span className="text-white font-black text-sm uppercase tracking-wider">Flexi / Pay As You Go — Most Popular</span>
                </div>
                <div className="bg-cl-tint px-6 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="font-black text-cl-dark text-base mb-1">Only pay for what we collect</p>
                    <ul className="flex flex-col gap-1">
                      {["Only pay minimum charge today (includes a single item)", "Option to add extra items now or pay on the day", "No hidden extras — price locked before we arrive"].map((b) => (
                        <li key={b} className="flex items-center gap-2 text-sm text-cl-dark/80 font-semibold">
                          <i className="ri-check-line text-cl-green flex-shrink-0"></i>{b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button onClick={onBookNow} className="bg-cl-green text-white font-black px-7 py-3.5 rounded-2xl text-sm whitespace-nowrap flex-shrink-0 cartoon-btn-green">
                    Book Flexi →
                  </button>
                </div>
              </div>
            )}

            {/* Callout charge notice
            {calloutCharge > 0 && (
              <div className="mb-8 bg-amber-50 border-2 border-amber-300 rounded-3xl px-6 py-4 flex items-start gap-4 cartoon-card">
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-400 text-white flex-shrink-0 mt-0.5">
                  <i className="ri-truck-line text-lg"></i>
                </span>
                <div>
                  <p className="font-black text-amber-900 text-base mb-0.5">£{calloutCharge} callout charge applies</p>
                  <p className="text-sm text-amber-800 font-semibold leading-relaxed">
                    A one-time £{calloutCharge} callout fee covers travel and labour. This is added once per booking — you can book just a single mattress for <strong>£{(32.50 + calloutCharge).toFixed(2)} total</strong> (£32.50 item + £{calloutCharge} callout).
                  </p>
                </div>
              </div>
            )} */}

            {/* Call-to-action banner */}
            <div className="bg-cl-dark rounded-3xl p-6 mb-10 flex flex-col sm:flex-row items-center justify-between gap-4 cartoon-card">
              <div>
                <p className="text-white font-black text-lg mb-0.5">
                  Starting from{" "}
                  <span className="text-cl-lime">
                    £{(Math.min(...items.map((i) => i.unitPrice)) + calloutCharge).toFixed(2)}
                  </span>
                </p>
                <p className="text-white/60 text-sm font-semibold">
                  Fixed price per item + £{calloutCharge} callout · Two-man team · Same-day available
                </p>
              </div>
              <button
                onClick={onBookNow}
                className="bg-cl-green text-white font-black px-8 py-3.5 rounded-2xl text-sm cartoon-btn-green whitespace-nowrap flex-shrink-0"
              >
                Book & Get Price →
              </button>
            </div>

            {/* Item grid by category */}
            <div className="space-y-8 mb-16">
              {categoryNames.map((cat) => (
                <div key={cat}>
                  <h3 className="font-black text-cl-dark text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                    <span className="w-5 h-0.5 bg-cl-green inline-block"></span>
                    {cat}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {categories[cat].map((item) => (
                      <button
                        key={item.id}
                        onClick={onBookNow}
                        className="bg-white rounded-2xl p-4 flex flex-col items-start gap-2 cartoon-card hover:border-cl-green transition-colors text-left"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-cl-lime border-2 border-cl-dark">
                          <i className={`${item.icon} text-cl-dark text-base`}></i>
                        </div>
                        <p className="text-cl-dark font-black text-xs leading-snug">{item.label}</p>
                        <p className="text-cl-green font-black text-base">
                          £{item.unitPrice % 1 === 0 ? item.unitPrice.toFixed(0) : item.unitPrice.toFixed(2)}
                        </p>
                        {item.note && (
                          <p className="text-gray-400 text-[10px] font-semibold leading-tight">{item.note}</p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <AcceptsPanel accepts={accepts} service={service} />
          </div>
        </section>
        <WaveDivider fromColor="#ffffff" toColor="#f3f4f8" flip />
      </div>
    );
  }

  // ── Fallback: no matching booking config found ───────────────────────────────
  return (
    <div>
      <section className="py-20 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <PricingHeader />
          <div className="text-center py-12">
            <button onClick={onBookNow} className="bg-cl-dark text-white font-black px-10 py-4 rounded-2xl text-base cartoon-btn">
              Get a Quote →
            </button>
          </div>
          <AcceptsPanel accepts={accepts} service={service} />
        </div>
      </section>
      <WaveDivider fromColor="#ffffff" toColor="#f3f4f8" flip />
    </div>
  );
}

// ── Shared sub-components ─────────────────────────────────────────────────────

function PricingHeader({ subtitle }: { subtitle?: string }) {
  return (
    <div className="text-center mb-14">
      <div className="inline-flex items-center gap-2 bg-cl-lime rounded-full px-4 py-1.5 mb-5 thick-border cartoon-shadow-sm">
        <span className="text-cl-dark text-xs font-black uppercase tracking-widest">Simple Pricing</span>
      </div>
      <h2 className="text-4xl md:text-5xl font-black text-cl-dark leading-tight mb-4">
        Honest prices,<br />no hidden extras.
      </h2>
      <p className="text-cl-dark/60 text-base font-semibold max-w-lg mx-auto">
        {subtitle ?? "Fixed-price quotes before we arrive. What you see is what you pay."}
      </p>
    </div>
  );
}

function AcceptsPanel({ accepts, service }: { accepts: string[]; service: ServicePageData }) {
  return (
    <div className="bg-cl-tint rounded-3xl p-8 thick-border cartoon-shadow">
      <div className="flex flex-col lg:flex-row lg:items-start gap-8">
        <div className="lg:w-64 flex-shrink-0">
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-cl-lime border-2 border-cl-dark mb-4 cartoon-shadow-sm">
            <i className="ri-checkbox-circle-line text-cl-dark text-2xl"></i>
          </div>
          <h3 className="text-cl-dark font-black text-xl mb-2">What we accept</h3>
          <p className="text-cl-dark/60 text-sm font-semibold leading-relaxed">
            {service.title} covers all of the following. Not sure? Call 03335 775445.
          </p>
        </div>
        <div className="flex-1 flex flex-wrap gap-3">
          {accepts.map((item) => (
            <div key={item} className="flex items-center gap-2 bg-white rounded-full px-4 py-2 thick-border cartoon-shadow-sm">
              <span className="w-5 h-5 flex items-center justify-center rounded-full bg-cl-green flex-shrink-0">
                <i className="ri-check-line text-white text-xs"></i>
              </span>
              <span className="text-cl-dark text-sm font-bold whitespace-nowrap">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}